import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Mail, Loader2, AlertCircle, CheckCircle2, X, Inbox } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { usePendingEmailDrafts, type PendingEmailDraft } from "@/hooks/use-pending-email-drafts";

interface GmailStatus {
  connected: boolean;
  userTokensConnected?: boolean;
  userTokensEmail?: string | null;
}

interface OutboxPulse {
  count: number;
  needsReconnect: boolean;
}

/**
 * Global modal that surfaces whenever the user has email drafts that were
 * held back because Gmail was disconnected at send-time. Provides a one-click
 * Connect path and, once Gmail is reconnected, lets the user re-send the
 * held messages without retyping anything.
 */
export function GmailReconnectPrompt() {
  const { isAuthenticated } = useAuth();
  const { drafts, removeDraft } = usePendingEmailDrafts();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryingAll, setRetryingAll] = useState(false);

  // Server-side Outbox pulse — surfaces automated sends queued by
  // recordFailedSend (drip, reminders, statements, etc) so the prompt
  // also fires when there are no localStorage drafts but the server has
  // pending items stuck on Gmail reconnect. Polled every 30s so a
  // sign-in lands on this prompt within a sweep tick.
  // Only enabled when authenticated: unauthenticated page visits (e.g.
  // /invoice/<token>) must not trigger SESSION_EXPIRED 401s that cause
  // the global queryClient interceptor to redirect to /login.
  const { data: outboxPulse } = useQuery<OutboxPulse>({
    queryKey: ["/api/email-drafts/pending-count"],
    enabled: isAuthenticated,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
    staleTime: 15000,
  });
  const serverPendingCount = outboxPulse?.count ?? 0;
  const serverNeedsReconnect = !!outboxPulse?.needsReconnect;

  // Poll Gmail status whenever drafts exist (local OR server-side) so the
  // "Send saved messages" CTA flips the moment the user reconnects.
  const hasAnyPending = drafts.length > 0 || serverPendingCount > 0;
  const { data: gmailStatus } = useQuery<GmailStatus>({
    queryKey: ["/api/gmail/status"],
    enabled: hasAnyPending,
    refetchInterval: hasAnyPending ? 5000 : false,
    staleTime: 0,
  });

  const connectGmailMutation = useMutation({
    mutationFn: async () => {
      // Preserve current page in returnUrl so the user lands back here after
      // OAuth completes (matches the behaviour in Settings).
      const currentUrl =
        typeof window !== "undefined"
          ? window.location.origin + window.location.pathname
          : "/";
      // Use raw fetch (GET needs no CSRF token) so we can inspect the HTTP
      // status and response body to distinguish auth failures from real errors.
      const res = await fetch(
        `/api/gmail/auth?returnUrl=${encodeURIComponent(currentUrl)}`,
        { credentials: "include" },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error(body.message || "Failed to initiate Gmail connection");
        (err as any).status = res.status;
        (err as any).code = body.code;
        throw err;
      }
      return res.json();
    },
    onSuccess: (data: { authUrl?: string }) => {
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      } else {
        toast({
          title: "Could not start Gmail connect",
          description: "Please open Settings and reconnect from there.",
          variant: "destructive",
        });
      }
    },
    onError: (error: any) => {
      // Session expired or workspace unresolvable (logged-out state).
      // Route the user through re-login so they can finish connecting Gmail.
      if (
        error?.status === 401 ||
        (error?.status === 400 && error?.code === "WORKSPACE_REQUIRED")
      ) {
        const returnTo = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `/api/login?silent=1&returnTo=${returnTo}`;
        return;
      }
      // Genuinely unexpected failure.
      toast({
        title: "Could not start Gmail connect",
        description: "Please open Settings and reconnect from there.",
        variant: "destructive",
      });
    },
  });

  // Open the dialog when EITHER source has items waiting. Server-side
  // pending items only force the prompt when Gmail is actually
  // disconnected — pure transient API errors auto-retry silently in the
  // background and don't need a modal at sign-in.
  const serverShouldPrompt = serverPendingCount > 0 && serverNeedsReconnect;
  const open = !dismissed && (drafts.length > 0 || serverShouldPrompt);
  // Mirror the Settings page: only the user's personal Gmail counts as "connected"
  // for the purpose of clearing the held drafts.
  const personalGmailConnected = !!gmailStatus?.userTokensConnected;

  async function retryDraft(draft: PendingEmailDraft): Promise<boolean> {
    try {
      await apiRequest(draft.method, draft.endpoint, draft.payload);
      removeDraft(draft.id);
      return true;
    } catch (err: any) {
      const msg = String(err?.message || "Failed to send.");
      toast({
        title: "Send failed",
        description: `${draft.recipientEmail}: ${msg}`,
        variant: "destructive",
      });
      return false;
    }
  }

  async function handleRetryOne(draft: PendingEmailDraft) {
    setRetryingId(draft.id);
    const ok = await retryDraft(draft);
    setRetryingId(null);
    if (ok) {
      toast({
        title: "Message sent",
        description: `Sent to ${draft.recipientEmail}.`,
      });
      // Refresh anything that might display the just-sent activity.
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    }
  }

  async function handleRetryAll() {
    setRetryingAll(true);
    let sent = 0;
    let failed = 0;
    for (const draft of drafts) {
      // eslint-disable-next-line no-await-in-loop
      const ok = await retryDraft(draft);
      if (ok) sent += 1;
      else failed += 1;
    }
    setRetryingAll(false);
    if (sent > 0) {
      toast({
        title: failed === 0 ? "All saved messages sent" : "Some messages sent",
        description:
          failed === 0
            ? `Sent ${sent} held message${sent === 1 ? "" : "s"}.`
            : `Sent ${sent}, ${failed} still pending.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/leads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    }
  }

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Allow user to dismiss the prompt; drafts remain in storage and the
        // dialog will resurface on next page load if Gmail is still
        // disconnected and there are still drafts.
        if (!o) setDismissed(true);
      }}
    >
      <DialogContent className="max-w-md" data-testid="dialog-gmail-reconnect-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {personalGmailConnected ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-amber-500" />
            )}
            {personalGmailConnected ? "Gmail reconnected" : "Gmail disconnected"}
          </DialogTitle>
          <DialogDescription>
            {personalGmailConnected
              ? `You have ${drafts.length} message${drafts.length === 1 ? "" : "s"} held while Gmail was disconnected. Send them now?`
              : `Your Gmail isn't connected, so ${drafts.length} message${drafts.length === 1 ? "" : "s"} ${drafts.length === 1 ? "is" : "are"} waiting. Reconnect to send.`}
          </DialogDescription>
        </DialogHeader>

        {/* Server-side Outbox pulse — only shows when automated sends
            (drip, reminders, statements) are also stuck on Gmail. */}
        {serverShouldPrompt ? (
          <div
            className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-sm flex items-center gap-2"
            data-testid="banner-outbox-pending"
          >
            <Inbox className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="flex-1">
              {serverPendingCount} automated send{serverPendingCount === 1 ? "" : "s"} queued in your Outbox.
            </span>
            <Link href="/inbox?folder=outbox">
              <Button
                size="sm"
                variant="link"
                className="h-auto p-0"
                onClick={() => setDismissed(true)}
                data-testid="link-open-outbox"
              >
                Open Outbox
              </Button>
            </Link>
          </div>
        ) : null}

        <ScrollArea className="max-h-64">
          <ul className="space-y-2 py-1">
            {drafts.map((draft) => (
              <li
                key={draft.id}
                className="flex items-start gap-2 rounded-md border border-border p-2 text-sm"
                data-testid={`pending-draft-${draft.id}`}
              >
                <Mail className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate" data-testid={`pending-draft-subject-${draft.id}`}>
                    {draft.subject || "(no subject)"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    To: {draft.recipientEmail}
                    {draft.description ? ` · ${draft.description}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {personalGmailConnected && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRetryOne(draft)}
                      disabled={retryingId === draft.id || retryingAll}
                      data-testid={`button-retry-draft-${draft.id}`}
                    >
                      {retryingId === draft.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send"}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeDraft(draft.id)}
                    aria-label="Discard saved message"
                    data-testid={`button-discard-draft-${draft.id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </ScrollArea>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            data-testid="button-dismiss-gmail-prompt"
          >
            Dismiss
          </Button>
          {personalGmailConnected ? (
            <Button
              onClick={handleRetryAll}
              disabled={retryingAll || drafts.length === 0}
              data-testid="button-send-all-saved-messages"
            >
              {retryingAll && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send saved {drafts.length === 1 ? "message" : `messages (${drafts.length})`}
            </Button>
          ) : (
            <Button
              onClick={() => connectGmailMutation.mutate()}
              disabled={connectGmailMutation.isPending}
              data-testid="button-connect-gmail-now"
            >
              {connectGmailMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Connect Gmail Now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
